<template>
  <button
    class="sb_button"
    :button-type="buttonType"
    :disabled="disabled"
    :class="{
      'v_icon-only': iconOnly,
      s_loading: loading,
      s_pulsating: pulsating,
    }"
    @click="!disabled && $emit('click')">
    <span class="sb_button_wrapper" :style="{ opacity: loading ? 0.5 : 1 }">
      <LoadingSpinner
        v-if="loading"
        :size="'27.2px'"
        :color="
          buttonType === 'primary' || buttonType === 'secondary'
            ? '#fff'
            : ' #040009'
        " />
      <slot />
    </span>
  </button>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner';

export default {
  components: {
    LoadingSpinner,
  },
  props: {
    /**
     * The type of the button, possible values: 'primary', 'secondary, 'naked', 'naked-body'
     */
    buttonType: {
      default: 'primary',
      validator: (value) =>
        [
          'default',
          'primary',
          'secondary',
          'tertiary',
          'naked',
          'naked-body',
          'naked-white',
          'naked-body-white',
        ].includes(value),
    },
    /**
     * The disabled state of the button
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * The pulsating state of the button
     */
    pulsating: {
      type: Boolean,
      default: false,
    },
    /**
     * The loading state of the button
     */
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconOnly: false,
    };
  },
  mounted() {
    const defaultSlot = this.$slots.default;
    if (defaultSlot.length === 1 && defaultSlot[0].componentOptions) {
      if (defaultSlot[0].componentOptions.tag === 'SvgIcon') {
        this.iconOnly = true;
      }
    }
  },
};
</script>

<style lang="scss">
.sb_button {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 60px;
  padding: 18px 32px;
  margin: 0;
  font-family: 'American Typewriter Bold', Arial, Helvetica, sans-serif;
  font-size: 22px;
  font-weight: 500;
  color: $default-text-color;
  text-align: left;
  text-decoration: none;
  letter-spacing: 0.5px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  background: none;
  background: $brand-white;
  border: none;
  border-radius: 9999px;
  outline: none;
  transition: opacity 0.3s ease;
  transition: color 0.2s ease, box-shadow 0.2s ease;

  &_wrapper {
    display: inline-grid;
    grid-template-rows: auto;
    grid-template-columns: 1fr;
    grid-auto-flow: column;
    grid-gap: 15px;
    align-items: center;
  }

  .sb_svg-icon {
    transform: scale(1.5);
  }

  &[button-type='primary'],
  &[button-type='secondary'],
  &[button-type='tertiary'] {
    color: $brand-white;
    text-shadow: 1px 1px 2px rgba($brand-black, 0.3);
    background-size: cover;
    box-shadow: 0 1px 4px 1px rgb(#000, 0.3);
    transition: color 0.3s ease, box-shadow 0.3s ease, filter 0.2s ease;

    &:hover,
    &:focus {
      box-shadow: 0 1px 4px 1px rgb(#000, 0.3), 0 4px 10px 1px rgb(#000, 0.3);
    }

    &:hover {
      color: $brand-creme;
    }

    &:active {
      box-shadow: 0 1px 1px 1px rgb(#000, 0.3);
      transform: translateY(1px);
    }

    &:disabled {
      color: rgba($brand-white, 0.7);
      filter: brightness(80%) grayscale(100%);
      box-shadow: none;
    }

    &.s_loading {
      padding: 18px;
    }
  }

  &[button-type='primary'] {
    background: url('data:image/jpeg;base64,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')
      center top 0;

    &:not(:disabled) {
      &.s_pulsating {
        animation: sb_btn-primary_pulsating 1.3s ease infinite;
      }
    }
  }

  &[button-type='secondary'] {
    background: url('data:image/jpeg;base64,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')
      center top 0;

    &:not(:disabled) {
      &.s_pulsating {
        animation: sb_btn-secondary_pulsating 1.3s ease infinite;
      }
    }
  }

  &[button-type='tertiary'] {
    background: url('data:image/jpeg;base64,/9j/4QAYRXhpZgAASUkqAAgAAAAAAAAAAAAAAP/sABFEdWNreQABAAQAAABCAAD/4QMcaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wLwA8P3hwYWNrZXQgYmVnaW49Iu+7vyIgaWQ9Ilc1TTBNcENlaGlIenJlU3pOVGN6a2M5ZCI/PiA8eDp4bXBtZXRhIHhtbG5zOng9ImFkb2JlOm5zOm1ldGEvIiB4OnhtcHRrPSJBZG9iZSBYTVAgQ29yZSA2LjAtYzAwMiA3OS4xNjQzNjAsIDIwMjAvMDIvMTMtMDE6MDc6MjIgICAgICAgICI+IDxyZGY6UkRGIHhtbG5zOnJkZj0iaHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyI+IDxyZGY6RGVzY3JpcHRpb24gcmRmOmFib3V0PSIiIHhtbG5zOnhtcE1NPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvbW0vIiB4bWxuczpzdFJlZj0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL3NUeXBlL1Jlc291cmNlUmVmIyIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bXBNTTpEb2N1bWVudElEPSJ4bXAuZGlkOjgxNUIzQTdBOTgxQjExRUFCMjI0ODJFQUVGMDZBREE4IiB4bXBNTTpJbnN0YW5jZUlEPSJ4bXAuaWlkOjgxNUIzQTc5OTgxQjExRUFCMjI0ODJFQUVGMDZBREE4IiB4bXA6Q3JlYXRvclRvb2w9IkFkb2JlIFBob3Rvc2hvcCAyMDIwIE1hY2ludG9zaCI+IDx4bXBNTTpEZXJpdmVkRnJvbSBzdFJlZjppbnN0YW5jZUlEPSI5RjFFQzMyQzUyMzM1QzAzODdCMzg3RTIzQUM1MkFCNSIgc3RSZWY6ZG9jdW1lbnRJRD0iOUYxRUMzMkM1MjMzNUMwMzg3QjM4N0UyM0FDNTJBQjUiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz7/7gAOQWRvYmUAZMAAAAAB/9sAhAAFAwMDBAMFBAQFBwUEBQcIBgUFBggKCAgICAgKDAoLCwsLCgwMDAwMDAwMDw8QEA8PFhUVFRYYGBgYGBgYGBgYAQUGBgoJChMMDBMUEQ4RFBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBj/wAARCABUANkDAREAAhEBAxEB/8QAawAAAwEBAQEBAAAAAAAAAAAAAAECAwQFBgcBAQEBAQEAAAAAAAAAAAAAAAABAgMGEAACAgICAgMAAQUAAAAAAAAAARECEgMhBDETQVFhUoGRIkIFEQEBAQEBAAAAAAAAAAAAAAAAARECEv/aAAwDAQACEQMRAD8A/NKh4N6HS6y2ptuK1Cxp2dFdTTq26W+/hgscjcNkFK7LBei2t7F7W1UqjY6Z2wbdJ4Ior4LBVXBpWtNqXl8F0Ts3TwvBm1NZmRRGRbYtdV9m22D7e6TPpWurZ7VFo5NIz26Um6uCMs3RgFdcgXXTVcvkBmlJoDN0eTgygWsB4DQsBoFQaDEaOerIy7el3FqbT5qwsrTd3ltrilFU5C1zZS2zOoasaIaf0GlJgVSy8Fgo3imRE5GbEwVJVoA0VKbaw3DNKxt0tifENExXRp0V1Vh82NDPa1a9mjDLNgVTwAnZI0qcwE7gNKeSMgAIoATAQHBS/Lqww2o5IqpAYbOr5SZrDHp7f+d166LOieSUqzYV59dn2RlasgLrf7LoHYaFJA6yy4gzouGwtVS1bKasIpbdq/2krRPbstwzNqWoZEiW0VU5sgWZrWiVzKUZIDTXIsZotYSETkDQ7sITu39v6QD9e/8AhYLjzE/kIuloK0tbCK1pZNAODWmtF29/r9ebxCpkyyYDV2iilsArJEBW6VGbVklLZztLVa005RZU1vP+JsqcmYZTe0BYzbDQkAkArWfLEGlFVeDVYrWhlmo7EJSCMcvwmNNOtq17VbK+OK4KuM+ttpr7FXZqJhthp3+yv8l/cGvm12PJcPJ12fBbCxvo1uy5+SYNFronCskyqbdquGQNbKgb9bW9+6uqvGT5ZEx3PqdWnGLt+tgxjv6mta3fXxHxIK5gAB1s1w/DAarR/IRStRAD25F0OSMsmytFJFIgANK+ChliNtXC5FYrHfsTfD4Cxjkg0TsiKK61bVa7slj4qBAHmG3Rr1lldDGemva7DT9dHCXloqRzVvZOZcma3Y9J2nr1s+HAYYZEHT0N/q7NLttLw/zID1L5ptrlPwwM9+xLRbLgDgpsqRKea5BgyBipAUgJ2AXtgIS2BTzAJAmSquu3HjyRF++qXCZYjLZuvZQ3CIMsii5IokBEBIHlnV1adfZjeSs9R0X0V2vKtkm/MsjGinW62tztum/iq+SNbT3dn2NVqooiayVCK0QGmvs76LGmxpfUgF92y/N7OzImJyBgTKpyA8gB2YBkRCTJUIqqyArIgJAUlBIEWYEyA5AcgGQCzX2B53J1x2TIF12WXgqWQpbIY0qzNZbarGGK0Vg0aZUOQIb5YDVgKVwDMAzAKsiU5AJAABWgB5oA9iAWbAkBSAsgJyZVIAA5LrG7RuV2wGxEmWlIMKXklStaWMMWNK2CNK2kmKbYESA0yoACQCQCQCQHn+ETBn+AwvYDB7AYWTLi4M2MMLL9IYCrgIABSUOSCOxTKsryi8uzkZ0UgADReRWDTIjVXM1ixVbmalC2SVVSA5AJAAhyAp/QAAkKJAWQBkASASApAJAZApLi4ze1cpBcRlb7ZDHWZbcV8c3iduWqzKABoCkGFIlSrRis0BGlcoAYAAAg5BTAXIAAAACYByAwAAAAMtuX9A3EIVqgMv/Z')
      center top 0;

    &:not(:disabled) {
      &.s_pulsating {
        animation: sb_btn-secondary_pulsating 1.3s ease infinite;
      }
    }
  }

  &[button-type='naked'] {
    padding: 0;
    color: $brand-primary-darkest;
    background: none;

    &:hover,
    &:focus {
      color: rgba($brand-primary-darkest, 0.7);
    }

    &:active {
      transform: translateY(1px);
    }

    &:disabled {
      color: rgba($brand-primary-darkest, 0.5);
    }
  }

  &[button-type='naked-white'] {
    padding: 0;
    color: $brand-white;
    background: none;

    &:hover,
    &:focus {
      color: rgba($brand-white, 0.7);
    }

    &:active {
      transform: translateY(1px);
    }

    &:disabled {
      color: rgba($brand-white, 0.5);
    }
  }

  &[button-type='naked-body'] {
    min-height: 0;
    padding: 0;
    font-family: Nunito, Arial, Helvetica, sans-serif;
    font-size: 16px;
    color: $brand-primary-darkest;
    letter-spacing: 0;
    background: none;

    .sb_button_wrapper {
      grid-gap: 10px;
    }

    .sb_svg-icon {
      // transform: scale(1);
    }

    &:hover,
    &:focus {
      color: rgba($brand-primary-darkest, 0.7);
    }

    &:active {
      transform: translateY(1px);
    }

    &:disabled {
      color: rgba($brand-primary-darkest, 0.5);
    }
  }

  &[button-type='naked-body-white'] {
    min-height: 0;
    padding: 0;
    font-family: Nunito, Arial, Helvetica, sans-serif;
    font-size: 16px;
    color: $brand-white;
    letter-spacing: 0;
    background: none;

    .sb_button_wrapper {
      grid-gap: 10px;
    }

    .sb_svg-icon {
      transform: scale(1);
    }

    &:hover,
    &:focus {
      color: rgba($brand-white, 0.7);
    }

    &:active {
      transform: translateY(1px);
    }

    &:disabled {
      color: rgba($brand-white, 0.5);
    }
  }

  //
  //  ICON ONLY
  //
  &.v_icon-only {
    padding: 18px;
  }

  //
  //  DISABLED
  //

  &:disabled {
    pointer-events: none;
  }
}

@keyframes sb_btn-primary_pulsating {
  0% {
    box-shadow: 0 0 0 0 rgba(#ef3d5f, 0);
  }

  50% {
    box-shadow: 0 0 60px 0 rgba(#ef3d5f, 1);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(#ef3d5f, 0);
  }
}
@keyframes sb_btn-secondary_pulsating {
  0% {
    box-shadow: 0 0 0 0 rgba(#6e2a73, 0);
  }

  50% {
    box-shadow: 0 0 60px 0 rgba(#6e2a73, 1);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(#6e2a73, 0);
  }
}
</style>
